import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/global.css';

const TABS = ['Dashboard', 'Landlords', 'Properties', 'Plans', 'ISPs', 'Tickets'];

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function AdminPanel() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [plans, setPlans] = useState([]);
  const [isps, setIsps] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    if (role !== 'admin') { navigate('/login'); return; }
    loadAll();
  }, [role]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l, p, pl, i, t] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/landlords'),
        api.get('/admin/properties'),
        api.get('/admin/plans'),
        api.get('/admin/isps'),
        api.get('/admin/tickets?status=open'),
      ]);
      setStats(s.data);
      setLandlords(l.data.landlords);
      setProperties(p.data.properties);
      setPlans(pl.data.plans);
      setIsps(i.data.isps);
      setTickets(t.data.tickets);
    } catch { toast.error('Could not load admin data'); }
    finally { setLoading(false); }
  };

  const loadTickets = async (status) => {
    setTicketFilter(status);
    try {
      const res = await api.get(`/admin/tickets?status=${status}`);
      setTickets(res.data.tickets);
    } catch { toast.error('Could not load tickets'); }
  };

  const updateTicket = async (id, status) => {
    try {
      await api.patch(`/admin/tickets/${id}`, { status });
      toast.success('Ticket updated');
      loadTickets(ticketFilter);
    } catch { toast.error('Could not update ticket'); }
  };

  const createLandlord = async (data) => {
    try {
      const res = await api.post('/admin/landlords', data);
      toast.success(`Landlord created. Setup link: ${res.data.setup_url}`);
      reset();
      setShowForm(false);
      const l = await api.get('/admin/landlords');
      setLandlords(l.data.landlords);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create landlord');
    }
  };

  const createPlan = async (data) => {
    try {
      await api.post('/admin/plans', {
        ...data,
        bulknet_price: parseFloat(data.bulknet_price),
        retail_price: parseFloat(data.retail_price),
        speed_mbps: parseInt(data.speed_mbps) || null,
        features: data.features ? data.features.split('\n').filter(Boolean) : [],
      });
      toast.success('Plan created');
      reset();
      setShowForm(false);
      const pl = await api.get('/admin/plans');
      setPlans(pl.data.plans);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create plan');
    }
  };

  const createISP = async (data) => {
    try {
      await api.post('/admin/isps', { ...data, revenue_share_pct: parseFloat(data.revenue_share_pct) });
      toast.success('ISP created');
      reset();
      setShowForm(false);
      const i = await api.get('/admin/isps');
      setIsps(i.data.isps);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create ISP');
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      {/* Admin topbar */}
      <div className="topbar">
        <div className="topbar-logo"><div className="topbar-logo-dot" />Affordable Telecom Admin</div>
        <div className="topbar-right">
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{user?.first_name} (Admin)</span>
          <button className="topbar-link" onClick={() => { localStorage.clear(); navigate('/login'); }}>Sign out</button>
        </div>
      </div>

      {/* Tab nav */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', gap: 0 }}>
        {TABS.map((t, i) => (
          <button key={i} onClick={() => { setTab(i); setShowForm(false); }}
            style={{
              padding: '14px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              color: tab === i ? 'var(--navy)' : 'var(--text-sub)',
              borderBottom: tab === i ? '2px solid var(--navy)' : '2px solid transparent',
              transition: 'all 0.1s'
            }}>
            {t}
            {t === 'Tickets' && stats?.open_tickets > 0 && (
              <span style={{ background: 'var(--red)', color: 'white', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '2px 6px', marginLeft: 6 }}>
                {stats.open_tickets}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: 28 }}>

        {/* ── DASHBOARD ── */}
        {tab === 0 && stats && (
          <div>
            <div className="page-title">Admin Dashboard</div>
            <div className="page-sub">Platform-wide overview</div>
            <div className="metric-grid">
              <div className="metric-card"><div className="metric-label">Total landlords</div><div className="metric-value">{stats.total_landlords}</div></div>
              <div className="metric-card"><div className="metric-label">Active properties</div><div className="metric-value accent">{stats.total_properties}</div></div>
              <div className="metric-card"><div className="metric-label">Active tenants</div><div className="metric-value green">{stats.total_active_tenants}</div></div>
              <div className="metric-card"><div className="metric-label">Revenue MTD</div><div className="metric-value sm">${fmt(stats.revenue_mtd)}</div></div>
              <div className="metric-card"><div className="metric-label">Open tickets</div><div className="metric-value" style={{ color: stats.open_tickets > 0 ? 'var(--red)' : 'var(--green)' }}>{stats.open_tickets}</div></div>
            </div>
          </div>
        )}

        {/* ── LANDLORDS ── */}
        {tab === 1 && (
          <div>
            <div className="flex-between" style={{ marginBottom: 20 }}>
              <div><div className="page-title">Landlords</div></div>
              <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); reset(); }}>
                {showForm ? 'Cancel' : '+ Add landlord'}
              </button>
            </div>

            {showForm && (
              <div className="card" style={{ marginBottom: 20, maxWidth: 560 }}>
                <div className="card-header"><div className="card-title">Create landlord account</div></div>
                <div className="card-body">
                  <form onSubmit={handleSubmit(createLandlord)}>
                    <div className="two-col">
                      <div className="form-group">
                        <label className="form-label">First name</label>
                        <input className={`form-input${errors.first_name ? ' error' : ''}`} {...register('first_name', { required: true })} />
                        {errors.first_name && <div className="form-error">Required</div>}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last name</label>
                        <input className={`form-input${errors.last_name ? ' error' : ''}`} {...register('last_name', { required: true })} />
                        {errors.last_name && <div className="form-error">Required</div>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email address</label>
                      <input type="email" className={`form-input${errors.email ? ' error' : ''}`} {...register('email', { required: true })} />
                      {errors.email && <div className="form-error">Required</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Company name</label>
                      <input className="form-input" {...register('company_name')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-input" {...register('phone')} />
                    </div>
                    <button type="submit" className="btn btn-primary">Create &amp; Send Setup Email</button>
                  </form>
                </div>
              </div>
            )}

            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Properties</th><th>Tenants</th><th>Status</th><th>Joined</th></tr></thead>
                  <tbody>
                    {landlords.map(l => (
                      <tr key={l.id}>
                        <td style={{ fontWeight: 600 }}>{l.first_name} {l.last_name}</td>
                        <td style={{ fontSize: 12 }}>{l.email}</td>
                        <td style={{ fontSize: 12 }}>{l.company_name || '—'}</td>
                        <td>{l.property_count}</td>
                        <td>{l.tenant_count}</td>
                        <td><span className={`badge badge-${l.status === 'active' ? 'green' : 'red'}`}>{l.status}</span></td>
                        <td style={{ fontSize: 12 }}>{fmtDate(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── PROPERTIES ── */}
        {tab === 2 && (
          <div>
            <div className="page-title" style={{ marginBottom: 20 }}>Properties</div>
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Property</th><th>Landlord</th><th>City, State</th><th>Units</th><th>Active</th><th>Slug</th><th>Status</th></tr></thead>
                  <tbody>
                    {properties.map(p => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td style={{ fontSize: 12 }}>{p.landlord_name}<br /><span style={{ color: 'var(--text-sub)' }}>{p.company_name}</span></td>
                        <td style={{ fontSize: 12 }}>{p.city}, {p.state}</td>
                        <td>{p.unit_count}</td>
                        <td style={{ color: 'var(--green)', fontWeight: 700 }}>{p.active_units}</td>
                        <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-sub)' }}>{p.slug}</td>
                        <td><span className={`badge badge-${p.status === 'active' ? 'green' : 'gray'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── PLANS ── */}
        {tab === 3 && (
          <div>
            <div className="flex-between" style={{ marginBottom: 20 }}>
              <div className="page-title">Service Plans</div>
              <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); reset(); }}>
                {showForm ? 'Cancel' : '+ Add plan'}
              </button>
            </div>

            {showForm && (
              <div className="card" style={{ marginBottom: 20, maxWidth: 600 }}>
                <div className="card-header"><div className="card-title">Create service plan</div></div>
                <div className="card-body">
                  <form onSubmit={handleSubmit(createPlan)}>
                    <div className="two-col">
                      <div className="form-group">
                        <label className="form-label">Plan name</label>
                        <input className="form-input" placeholder="Single Play" {...register('name', { required: true })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Tier</label>
                        <select className="form-input" {...register('tier', { required: true })}>
                          <option value="single">Single Play</option>
                          <option value="double">Double Play</option>
                          <option value="triple">Triple Play</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">ISP</label>
                      <select className="form-input" {...register('isp_id')}>
                        <option value="">No ISP assigned</option>
                        {isps.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                    </div>
                    <div className="two-col">
                      <div className="form-group">
                        <label className="form-label">Affordable Telecom price ($/mo)</label>
                        <input type="number" step="0.01" className="form-input" placeholder="45.00" {...register('bulknet_price', { required: true })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Retail price ($/mo)</label>
                        <input type="number" step="0.01" className="form-input" placeholder="89.99" {...register('retail_price', { required: true })} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Speed (Mbps)</label>
                      <input type="number" className="form-input" placeholder="500" {...register('speed_mbps')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Features (one per line)</label>
                      <textarea className="form-input" rows={4}
                        placeholder={"High-speed internet 500Mbps\nUnlimited data\nFree modem/router\n24/7 support"}
                        {...register('features')} />
                    </div>
                    <button type="submit" className="btn btn-primary">Create Plan</button>
                  </form>
                </div>
              </div>
            )}

            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Tier</th><th>ISP</th><th>Our Price</th><th>Retail</th><th>Savings</th><th>Status</th></tr></thead>
                  <tbody>
                    {plans.map(p => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td style={{ textTransform: 'capitalize' }}>{p.tier}</td>
                        <td style={{ fontSize: 12 }}>{p.isp_name || '—'}</td>
                        <td style={{ fontWeight: 700, color: 'var(--navy)' }}>${fmt(p.bulknet_price)}</td>
                        <td style={{ color: 'var(--text-sub)', textDecoration: 'line-through' }}>${fmt(p.retail_price)}</td>
                        <td style={{ color: 'var(--green)', fontWeight: 700 }}>${fmt(p.retail_price - p.bulknet_price)}/mo</td>
                        <td><span className={`badge badge-${p.status === 'active' ? 'green' : 'gray'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── ISPs ── */}
        {tab === 4 && (
          <div>
            <div className="flex-between" style={{ marginBottom: 20 }}>
              <div className="page-title">ISPs</div>
              <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); reset(); }}>
                {showForm ? 'Cancel' : '+ Add ISP'}
              </button>
            </div>

            {showForm && (
              <div className="card" style={{ marginBottom: 20, maxWidth: 480 }}>
                <div className="card-header"><div className="card-title">Add ISP</div></div>
                <div className="card-body">
                  <form onSubmit={handleSubmit(createISP)}>
                    <div className="form-group">
                      <label className="form-label">ISP name</label>
                      <input className="form-input" placeholder="Optimum" {...register('name', { required: true })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Contact name</label>
                      <input className="form-input" {...register('contact_name')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Contact email</label>
                      <input type="email" className="form-input" {...register('contact_email')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Revenue share % (paid to Affordable Telecom)</label>
                      <input type="number" step="0.01" className="form-input" defaultValue="15" {...register('revenue_share_pct')} />
                    </div>
                    <button type="submit" className="btn btn-primary">Add ISP</button>
                  </form>
                </div>
              </div>
            )}

            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Contact</th><th>Contact Email</th><th>Rev Share %</th><th>Status</th></tr></thead>
                  <tbody>
                    {isps.map(i => (
                      <tr key={i.id}>
                        <td style={{ fontWeight: 600 }}>{i.name}</td>
                        <td>{i.contact_name || '—'}</td>
                        <td style={{ fontSize: 12 }}>{i.contact_email || '—'}</td>
                        <td style={{ fontWeight: 700, color: 'var(--teal)' }}>{i.revenue_share_pct}%</td>
                        <td><span className={`badge badge-${i.status === 'active' ? 'green' : 'gray'}`}>{i.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── TICKETS ── */}
        {tab === 5 && (
          <div>
            <div className="flex-between" style={{ marginBottom: 20 }}>
              <div className="page-title">Support Tickets</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['open', 'in_progress', 'resolved', 'all'].map(s => (
                  <button key={s} className={`btn btn-sm ${ticketFilter === s ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => loadTickets(s)}>
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Date</th><th>Tenant</th><th>Property / Unit</th><th>Subject</th><th>Category</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {tickets.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No tickets found</td></tr>
                    )}
                    {tickets.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontSize: 12 }}>{fmtDate(t.created_at)}</td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{t.tenant_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{t.tenant_email}</div>
                        </td>
                        <td style={{ fontSize: 12 }}>{t.property_name}<br />{t.unit_number}</td>
                        <td style={{ maxWidth: 200 }}><div style={{ fontSize: 13 }}>{t.subject}</div><div style={{ fontSize: 11, color: 'var(--text-sub)', marginTop: 2 }}>{t.description?.substring(0, 80)}...</div></td>
                        <td style={{ textTransform: 'capitalize', fontSize: 12 }}>{t.category}</td>
                        <td><span className={`badge badge-${t.status === 'resolved' ? 'green' : t.status === 'open' ? 'blue' : 'orange'}`}>{t.status.replace('_', ' ')}</span></td>
                        <td>
                          <select className="form-input" style={{ fontSize: 12, padding: '4px 8px', width: 120 }}
                            value={t.status} onChange={e => updateTicket(t.id, e.target.value)}>
                            <option value="open">Open</option>
                            <option value="in_progress">In progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
